import { useQueries } from "@tanstack/react-query";
import { SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useAddress } from "#modules/addresses/view/useAddress";
import { ClientDto } from "#modules/clients/infra/Client.dto";
import { useClient } from "#modules/clients/view/useClient";
import { useCreateClient } from "#modules/clients/view/useCreateClient";
import { useUpdateClientMutation } from "#modules/clients/view/useUpdateClientMutation";
import { ContactDto } from "#modules/contacts/infra/Contact.dto";
import { useContact } from "#modules/contacts/view/useContact";
import { useCreateContact } from "#modules/contacts/view/useCreateContact";
import { useUpdateContact } from "#modules/contacts/view/useUpdateContact";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { UpdateQuoteRequestDto } from "#modules/quoteRequests/infra/updateQuoteRequest.api";
import { GenerateQuoteForm, PdfInputs } from "#modules/quoteRequests/view/GenerateQuoteForm";
import { useQuoteRequest } from "#modules/quoteRequests/view/useQuoteRequest";
import { useQuoteRequestLinesSorting } from "#modules/quoteRequests/view/useQuoteRequestLinesSorting";
import { useUpdateQuoteRequestInfos } from "#modules/quoteRequests/view/useUpdateQuoteRequestInfos";
import { useCreateQuote } from "#modules/quotes/view/useCreateQuote";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { Modal } from "#shared/view/components/Modal";

export const GenerateQuoteInfoModal = ({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { quoteRequestId } = useParams();
  const { quoteRequest } = useQuoteRequest({ quoteRequestId });
  const quoteRequestLineResponses = useQueries({
    queries:
      quoteRequest?.quoteRequestLines.map((line) => ({
        ...quoteRequestLineQuery({ id: line.id }),
        enabled: !!opened,
      })) ?? [],
  });

  const quoteRequestLines = quoteRequestLineResponses
    .map((response) => response.data)
    .filter((qrl) => qrl !== undefined && qrl !== null);

  const { client } = useClient(quoteRequest?.clientId ?? undefined);
  const { address } = useAddress(quoteRequest?.billingAddressId ?? undefined);
  const { contact } = useContact(quoteRequest?.contactId ?? undefined);

  const { batches } = useQuoteRequestLinesSorting(quoteRequestLines);

  const { updateClientAsync, isPending: isUpdatingClient } = useUpdateClientMutation({
    clientId: quoteRequest?.clientId || "",
  });

  const { createClientAsync, isPending: isCreatingClient } = useCreateClient({
    options: { shouldShowSuccessToast: false },
  });

  const { createContactAsync } = useCreateContact({
    clientId: quoteRequest?.clientId || "",
  });

  const { updateContactAsync } = useUpdateContact({
    clientId: quoteRequest?.clientId || "",
    contactId: quoteRequest?.contactId || "",
  });

  const { updateQuoteRequestInfosAsync, isPending: isUpdatingQuoteRequestInfos } =
    useUpdateQuoteRequestInfos({
      shouldShowSuccessToast: false,
    });

  const { mutate: createQuoteMutation, isPending: isCreatingQuote } = useCreateQuote(
    quoteRequest?.id ?? "",
    {
      onSuccess: () => {
        analytics.capture(AnalyticsEvent.QuoteGenerated, {
          quote_request_id: quoteRequest?.id,
          line_count: quoteRequestLines.filter(Boolean).length,
        });
        onClose();
      },
    },
  );

  const handleCreateQuote = () => {
    const quoteBatches = Array.from(batches.entries()).map(([partId, batchAssemblies]) => ({
      partId,
      assemblies: batchAssemblies.map((assembly) => ({
        parentId: assembly.quoteRequestLine.id,
        children: assembly.children.map((child) => child.quoteRequestLine.id),
      })),
    }));

    createQuoteMutation({
      batches: quoteBatches,
    });
  };

  const onSubmit: SubmitHandler<PdfInputs> = async (quoteRequestData) => {
    const newClient = await setClient(quoteRequestData);

    const newContact = await setContact(quoteRequestData, newClient?.id ?? "");

    const input: UpdateQuoteRequestDto = {
      id: quoteRequest?.id ?? "",
      clientId: newClient?.id ?? "",
      contactId: newContact?.id ?? "",
      billingAddressId: newClient?.addresses?.[0]?.id ?? "",
      expirationDate: quoteRequestData.expirationDate?.toISOString(),
      salesMarginRatio: quoteRequestData.salesMarginRatio ?? undefined,
      discountRatio: quoteRequestData.discountRatio ?? undefined,
      clientReference: quoteRequestData.clientReference ?? undefined,
      comment: quoteRequestData.comment ?? undefined,
    };

    if (quoteRequestLines.length > 0) {
      input.quoteRequestLines = quoteRequestLines
        .filter((line): line is NonNullable<typeof line> => !!line)
        .map((line) => ({
          id: line.id,
          shippingDelay: quoteRequestData.shippingDelay?.toISOString(),
        }));
    }

    await updateQuoteRequestInfosAsync(input);

    handleCreateQuote();
  };

  const setClient = async (quoteRequestData: PdfInputs) => {
    let newClient: ClientDto | null = null;

    const clientAlreadyExists = quoteRequestData.client?.id ? true : false;

    if (clientAlreadyExists) {
      newClient = await updateClientAsync({
        id: quoteRequestData.client!.id,
        client: {
          name: quoteRequestData.client?.name ?? "",
          address: {
            id: quoteRequestData.billingAddress?.id,
            street: quoteRequestData.billingAddress?.street ?? "",
            zipCode: quoteRequestData.billingAddress?.zipCode ?? "",
            city: quoteRequestData.billingAddress?.city ?? "",
          },
        },
      });
    } else {
      newClient = await createClientAsync({
        name: quoteRequestData.client?.name ?? "",
        address: {
          street: quoteRequestData.billingAddress?.street ?? "",
          zipCode: quoteRequestData.billingAddress?.zipCode ?? "",
          city: quoteRequestData.billingAddress?.city ?? "",
        },
      });
    }

    return newClient;
  };

  const setContact = async (quoteRequestData: PdfInputs, clientId: string) => {
    let newContact: ContactDto | null = null;

    const contactAlreadyExists = quoteRequestData.contact?.id ? true : false;

    if (contactAlreadyExists) {
      newContact = await updateContactAsync({
        id: quoteRequestData.contact?.id ?? "",
        contact: {
          firstName: quoteRequestData.contact?.firstName ?? "",
          lastName: quoteRequestData.contact?.lastName ?? "",
          email: quoteRequestData.contact?.email ?? "",
          phone: quoteRequestData.contact?.phone ?? "",
        },
      });
    } else {
      newContact = await createContactAsync({
        clientId,
        firstName: quoteRequestData.contact?.firstName ?? "",
        lastName: quoteRequestData.contact?.lastName ?? "",
        email: quoteRequestData.contact?.email ?? "",
        phone: quoteRequestData.contact?.phone ?? "",
      });
    }

    return newContact;
  };

  const quoteCreationPending =
    isUpdatingClient || isCreatingClient || isUpdatingQuoteRequestInfos || isCreatingQuote;

  return (
    <Modal
      opened={opened}
      title={t("quoteRequestLine.generateQuoteModal.title")}
      onClose={onClose}
      size="xl"
    >
      <GenerateQuoteForm
        quoteRequest={quoteRequest}
        client={client}
        address={address}
        contact={contact}
        onSubmit={onSubmit}
        isLoading={quoteCreationPending}
      />
    </Modal>
  );
};
